import { useEffect, useState } from "react";
import api from "@/api/client";
import { Sparkles } from "lucide-react";

/**
 * Fetches courier + service_type suggestions for a pincode and renders a
 * columnar rate breakdown with Best Value / Fastest / Cheapest badges.
 *
 * Props:
 *   pincode, source_pincode, weight_kg, mode, urgency
 *   selectedCourier, selectedService  — to highlight the currently picked row
 *   onPick(courier, service_type)     — invoked when the user picks a row
 */
const BADGE_STYLES = {
  best_value: "bg-emerald-100 text-emerald-800 border-emerald-300",
  fastest:    "bg-sky-100 text-sky-800 border-sky-300",
  cheapest:   "bg-amber-100 text-amber-800 border-amber-300",
};

export default function RateSuggestion({
  pincode, source_pincode, weight_kg, mode, urgency, service_type,
  selectedCourier, selectedService, onPick,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const clean = (pincode || "").trim();
    if (clean.length < 3) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rates/suggest", {
          params: {
            destination_pincode: clean,
            source_pincode: (source_pincode || "").trim() || undefined,
            weight_kg: weight_kg || 0.5,
            mode: mode || undefined,
            urgency: urgency || "standard",
            service_type: service_type || undefined,   // STRICT: Air-DOC pick → Air-DOC matrix only, no Surface fallback
          },
        });
        setData(data);
      } catch { setData(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [pincode, source_pincode, weight_kg, mode, urgency, service_type]);

  if (!pincode || pincode.length < 3) return null;
  if (loading && !data) {
    return (
      <div className="border border-border rounded-md p-3 text-xs text-muted-foreground bg-card" data-testid="rate-suggest-loading">
        Analyzing rates for pincode {pincode}…
      </div>
    );
  }
  // Zero-fallback empty state — makes the "no Surface rate stand-in" rule visible.
  if (!data || !data.options || data.options.length === 0) {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-xs text-amber-900"
           data-testid="rate-suggest-empty">
        <b>Rate not configured</b>
        {service_type ? <> for <span className="mono">{service_type}</span></> : null}
        {" · PIN "}{pincode}
        . Configure a rate cell in <b>Rate Matrix</b> for this courier + service + zone. The engine will NOT fall back to a different Ship Product.
      </div>
    );
  }

  const options = data.options;
  const idxBestValue = options.reduce((mi, r, i) => Number(r.price) < Number(options[mi].price) ? i : mi, 0);
  const idxFastest   = options.reduce((mi, r, i) => Number(r.eta_days) < Number(options[mi].eta_days) ? i : mi, 0);
  const idxCheapest  = options.reduce((mi, r, i) => Number(r.base) < Number(options[mi].base) ? i : mi, 0);

  const badgesFor = (i) => {
    const b = [];
    if (i === idxBestValue) b.push({ key: "best_value", label: "Best Value" });
    if (i === idxFastest)   b.push({ key: "fastest",    label: "Fastest"    });
    if (i === idxCheapest)  b.push({ key: "cheapest",   label: "Cheapest"   });
    return b;
  };

  // Row is picked only when BOTH courier and service_type match — this is the fix
  // for the "Standard vs Express same courier" selection bug. Selecting Surface for
  // BlueDart must NOT also mark BlueDart Express as picked.
  const isPicked = (r) =>
    selectedCourier === r.courier &&
    (selectedService ? selectedService === r.service_type : false);

  return (
    <div className="border border-border rounded-md p-4 bg-card card-elev" data-testid="rate-suggest">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-accent" />
          <div className="overline">Rate comparison · {data.zone} zone</div>
        </div>
        <span className="mono text-[11px] text-muted-foreground">
          PIN {data.pincode} · {data.weight_kg}kg · {options.length} option{options.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-xs" data-testid="rate-suggest-table">
          <thead className="bg-muted text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 text-left">Courier · Service</th>
              <th className="p-2 text-left">Tags</th>
              <th className="p-2 text-right mono">Base ₹</th>
              <th className="p-2 text-right mono">Fuel ₹</th>
              <th className="p-2 text-right mono">GST ₹</th>
              <th className="p-2 text-right mono font-bold">Total ₹</th>
              <th className="p-2 text-right mono">ETA</th>
              <th className="p-2 w-24" />
            </tr>
          </thead>
          <tbody>
            {options.map((r, i) => {
              const picked = isPicked(r);
              const badges = badgesFor(i);
              const rowKey = `${r.courier}-${r.service_type || r.mode || i}`;
              return (
                <tr key={rowKey}
                    data-testid={`rate-suggest-row-${rowKey}`}
                    className={`border-t border-border transition ${picked ? "bg-primary/10" : "hover:bg-muted/30"}`}>
                  <td className="p-2">
                    <div className="font-bold">{r.courier}</div>
                    <div className="text-[10px] text-muted-foreground">{r.service_type || r.mode}</div>
                  </td>
                  <td className="p-2">
                    <div className="flex flex-wrap gap-1">
                      {badges.map(b => (
                        <span key={b.key}
                              data-testid={`rate-badge-${b.key}-${rowKey}`}
                              className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${BADGE_STYLES[b.key]}`}>
                          {b.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-2 text-right mono">{Number(r.base || 0).toFixed(2)}</td>
                  <td className="p-2 text-right mono">{Number(r.fuel || 0).toFixed(2)}</td>
                  <td className="p-2 text-right mono">{Number(r.gst || 0).toFixed(2)}</td>
                  <td className="p-2 text-right mono font-bold">{Number(r.price).toFixed(2)}</td>
                  <td className="p-2 text-right mono text-[11px]">{
                    r.eta_min_days != null && r.eta_max_days != null && r.eta_min_days !== r.eta_max_days
                      ? `${r.eta_min_days}–${r.eta_max_days}d`
                      : `${r.eta_days}d`
                  }</td>
                  <td className="p-2 text-right">
                    {picked ? (
                      <span data-testid={`rate-picked-${rowKey}`}
                            className="text-[9px] font-bold text-primary bg-red-50 border border-primary px-1.5 py-0.5 rounded">
                        SELECTED
                      </span>
                    ) : (
                      <button type="button"
                              data-testid={`rate-pick-${rowKey}`}
                              onClick={() => onPick && onPick(r.courier, r.service_type)}
                              className="text-[10px] font-bold text-primary border border-primary/40 rounded px-2 py-1 hover:bg-primary hover:text-primary-foreground transition">
                        Pick
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2" data-testid="rate-suggest-legend">
        <b>Best Value</b> = lowest total · <b>Fastest</b> = lowest ETA · <b>Cheapest</b> = lowest base freight
      </div>
    </div>
  );
}
